import { inject, Injectable } from '@angular/core';
import { TenantSettingsStore } from 'app/core/tenant-settings/store/tenant-settings.store';
import { UserStore } from 'app/modules/users/store/user.store';
import { Observable, of } from 'rxjs';
import { User } from 'app/shared/types/user.types';
import { safeReturnUrl } from 'app/shared/utils/navigation-state.utils';

// MVC LogoutReasonTypes.Session_Expired
const SESSION_EXPIRED_LOGOUT_REASON = 3;

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _userStore = inject(UserStore);
    private _tenantSettingsStore = inject(TenantSettingsStore);

    signOut(logoutReason?: number | null, returnUrl?: string | null): void {
        const query = new URLSearchParams();
        if (logoutReason != null) query.set('reason', `${logoutReason}`);
        if (returnUrl) query.set('ReturnUrl', returnUrl);
        const queryString = query.toString();
        window.location.href = `/authentication/logout${queryString ? `?${queryString}` : ''}`;
    }

    // API 401: always go through MVC logout so its cookie is cleared, carrying ReturnUrl so login lands back on this page.
    // Going straight to login loops forever when MVC still accepts the cookie the API rejected (Login GET redirects
    // authenticated users straight back to ReturnUrl). No logoutReason means the session wasn't accepted, so audit as expired.
    handleUnauthorized401(errorBody: unknown): void {
        const logoutReason = (errorBody as { logoutReason?: unknown } | null)?.logoutReason;
        const returnUrl = safeReturnUrl(`${window.location.pathname}${window.location.search}`);
        this.signOut(typeof logoutReason === 'number' ? logoutReason : SESSION_EXPIRED_LOGOUT_REASON, returnUrl);
    }

    async authenticate(): Promise<User> {
        const user = await this._userStore.loadUser();
        this._authenticated = true;
        await this._tenantSettingsStore.loadTenantSettings();
        return user;
    }

    check(): Observable<boolean> {
        return of(this._authenticated);
    }
}
